import constants from 'common/constants'
import {
  isNetworkError,
  markFirstSuccessfulRequest,
  shouldReportConnectivityError,
} from 'common/helpers/connectivity'
import {clearNetworkError, setNetworkError} from 'setup/redux/Actions/Connectivity/connectivity'
import store from 'setup/redux/Store'

import {AuthModel} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

/** True when a request URL targets our own API (relative, or same origin as the API base) */
function isOwnApiUrl(url: unknown): boolean {
  if (typeof url !== 'string' || !url) return false
  if (!/^https?:\/\//i.test(url)) return true
  try {
    return new URL(url).origin === new URL(constants.api_base_url).origin
  } catch {
    return false
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getAuth()
      if (auth && auth.api_token) {
        config.headers.Authorization = `Bearer ${auth.api_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => {
      markFirstSuccessfulRequest()
      store.dispatch(clearNetworkError())
      return response
    },
    (error: any) => {
      // Only requests to our own API count as backend connectivity issues.
      // Axios is also used for direct uploads to third-party hosts (e.g.
      // presigned S3 PUTs); those failing must not trigger the "server
      // unreachable" banner while the API itself is fine.
      const isApiRequest = isOwnApiUrl(error?.config?.url)
      if (isApiRequest && isNetworkError(error) && shouldReportConnectivityError()) {
        const message =
          typeof window !== 'undefined' && (window as any).intl
            ? (window as any).intl.$t({id: 'CONNECTIVITY.API_UNAVAILABLE'})
            : 'Unable to connect. Please check your internet connection.'
        store.dispatch(setNetworkError(message))
      }
      return Promise.reject(error)
    }
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
